import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper
} from '@mui/material';
import { DirectionsBoat, Security, Star, LocationOn, Phone, Email } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Counter component with animation
const AnimatedCounter = ({ target, suffix = '', duration = 2000, startOnView = true }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const animate = () => {
      const numericTarget = parseFloat(target);
      const startTime = Date.now();
      const startValue = 0;

      const updateCount = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = startValue + (numericTarget - startValue) * easeOut;

        setCount(Math.floor(currentValue));

        if (progress < 1) {
          requestAnimationFrame(updateCount);
        } else {
          setCount(numericTarget);
        }
      };

      requestAnimationFrame(updateCount);
    };

    if (!startOnView || hasAnimated) {
      // If not using view-based animation or already animated, start immediately
      if (!hasAnimated) {
        animate();
        setHasAnimated(true);
      }
      return;
    }

    // Intersection Observer for view-based animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            animate();
            setHasAnimated(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    const observedElement = elementRef.current;
    if (observedElement) {
      observer.observe(observedElement);
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement);
      }
    };
  }, [startOnView, hasAnimated, target, duration]);

  return (
    <span ref={elementRef}>
      {count}{suffix}
    </span>
  );
};

const About = () => {
  // Brand colors: Orange and Navy Blue
  const brandOrange = '#FF6B35';
  const brandNavy = '#1a237e';

  const stats = [
    { number: 22, suffix: '', label: 'Lata Doświadczenia' },
    { number: 9, suffix: '', label: 'Jachtów we Flocie' },
    { number: 100, suffix: '%', label: 'Bezpieczeństwo' }
  ];

  const values = [
    {
      icon: <DirectionsBoat sx={{ fontSize: 48, color: brandNavy }} />,
      title: 'Doświadczenie',
      description: 'Wieloletnie doświadczenie w branży żeglarskiej pozwala nam oferować profesjonalne usługi.'
    },
    {
      icon: <Star sx={{ fontSize: 48, color: brandOrange }} />,
      title: 'Jakość',
      description: 'Stawiamy na najwyższą jakość obsługi i komfort naszych klientów podczas każdego rejsu.'
    },
  
    {
      icon: <Security sx={{ fontSize: 48, color: brandNavy }} />,
      title: 'Bezpieczeństwo',
      description: 'Wszystkie nasze jachty są regularnie serwisowane i wyposażone w najnowsze systemy bezpieczeństwa.'
    }
  ];

  return (
    <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: brandNavy,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            O Nas
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Jesteśmy rodzinną firmą z pasją do żeglowania i Mazur. Od lat czarterujemy jachty, pomagając naszym klientom odkrywać urok mazurskich jezior w komfortowy i bezpieczny sposób. Stawiamy na sprawdzony sprzęt, indywidualne podejście i przyjazną atmosferę — bo wiemy, że najlepsze rejsy zaczynają się od zaufania. Zapraszamy na wodę!
          </Typography>
        </Box>

        {/* Stats */}
        <Grid 
          container 
          spacing={{ xs: 2, sm: 3, md: 4 }} 
          sx={{ 
            mb: 8,
            justifyContent: 'center'
          }}
        >
          {stats.map((stat, index) => (
            <Grid item xs={6} sm={6} md={4} key={index}>
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  backgroundColor: 'white',
                  border: `1px solid ${brandNavy}33`,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 32px ${brandNavy}4D, 0 4px 12px rgba(0,0,0,0.12)`,
                    border: `1px solid ${brandNavy}66`
                  }
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: brandNavy,
                    mb: 1,
                    minHeight: '3.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AnimatedCounter 
                    target={stat.number} 
                    suffix={stat.suffix}
                    duration={2500}
                  />
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666',
                    fontWeight: 500
                  }}
                >
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Values */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {values.map((value, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: 'white',
                  border: index === 1 ? `1px solid ${brandOrange}33` : `1px solid ${brandNavy}33`,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: index === 1 ? `0 12px 32px ${brandOrange}4D, 0 4px 12px rgba(0,0,0,0.12)` : `0 12px 32px ${brandNavy}4D, 0 4px 12px rgba(0,0,0,0.12)`,
                    border: index === 1 ? `1px solid ${brandOrange}66` : `1px solid ${brandNavy}66`
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    {value.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: brandNavy,
                      mb: 2
                    }}
                  >
                    {value.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      lineHeight: 1.6
                    }}
                  >
                    {value.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Map Section */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: brandNavy,
              mb: 3,
              textAlign: 'center'
            }}
          >
            Lokalizacja
          </Typography>
          
          {/* Address Card */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: 'white',
                  border: `1px solid ${brandNavy}33`,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px ${brandNavy}4D, 0 4px 12px rgba(0,0,0,0.12)`,
                    border: `1px solid ${brandNavy}66`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <LocationOn sx={{ fontSize: 40, color: brandNavy, mr: 2 }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: brandNavy
                    }}
                  >
                    Ryn Marina
                  </Typography>
                </Box>
                <Box sx={{ pl: 7 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      lineHeight: 2,
                      mb: 2
                    }}
                  >
                    <strong>Adres:</strong><br />
                    ul. Mazurska 17<br />
                    11-520 Ryn<br />
                    Polska
                  </Typography>
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone sx={{ fontSize: 20, color: brandNavy, mr: 1.5 }} />
                        <Typography variant="body1" sx={{ color: '#666' }}>
                          +48 601 916 163
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone sx={{ fontSize: 20, color: brandNavy, mr: 1.5 }} />
                        <Typography variant="body1" sx={{ color: '#666' }}>
                          +48 607 526 312
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Email sx={{ fontSize: 20, color: brandOrange, mr: 1.5 }} />
                        <Typography variant="body1" sx={{ color: '#666' }}>
                          madacar@wp.pl
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Email sx={{ fontSize: 20, color: brandOrange, mr: 1.5 }} />
                        <Typography variant="body1" sx={{ color: '#666' }}>
                          madacar@o2.pl
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={4}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: { xs: '400px', md: '100%', minHeight: '300px' },
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
            <MapContainer
              center={[53.9321405005409, 21.53787625443813]} // Native Marine location (Giżycko area, Masurian Lakes)
              zoom={13}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[53.9321405005409, 21.53787625443813]}>
                <Popup>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Ryn Marina
                  </Typography>
                  <Typography variant="body2">
                    Lokalizacja naszego portu
                  </Typography>
                </Popup>
              </Marker>
            </MapContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
 
      </Container>
    </Box>
  );
};

export default About;
